import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, User, Clock, CalendarDays } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = ["GK", "DG", "DC", "DC", "DD", "6", "8", "8", "AG", "AD", "9"];
const SLOT_LABELS = {
  GK: "Gardien", DG: "Latéral gauche", DC: "Défenseur central", DD: "Latéral droit",
  "6": "Milieu défensif (6)", "8": "Milieu (8)", AG: "Ailier gauche", AD: "Ailier droit", "9": "Attaquant (9)",
};

function age(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / 31557600000);
}

export default function TeamPage() {
  const { id } = useParams();
  const [team, setTeam] = useState(null);
  const [error, setError] = useState(false);
  const [equipesVisibles, setEquipesVisibles] = useState(true);
  const { t } = useLang();

  useEffect(() => {
    axios.get(`${API}/teams/${id}`).then((r) => setTeam(r.data)).catch(() => setError(true));
    axios.get(`${API}/settings`).then((r) => setEquipesVisibles(r.data.equipes_visibles !== false)).catch(() => {});
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] text-white flex flex-col items-center justify-center gap-6">
        <p className="text-[#71717A]">{t("teampage.notfound")}</p>
        <Link to="/" data-testid="team-back-home" className="text-[#C5A059] text-xs uppercase tracking-[0.25em] border border-[#C5A059] px-6 py-3 hover:bg-[#C5A059] hover:text-black transition-colors">{t("teampage.home")}</Link>
      </div>
    );
  }
  if (!team) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
        <p className="text-[#71717A] text-xs uppercase tracking-[0.3em]">Chargement…</p>
      </div>
    );
  }
  if (team.publiee === false || !equipesVisibles) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] text-white flex flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="font-display text-3xl uppercase font-bold">{t("teampage.unpublished")}</p>
        <p className="text-[#A1A1AA] text-sm max-w-md leading-relaxed">{t("teampage.unpublished.x")}</p>
        <Link to="/detection" data-testid="team-unpublished-cta" className="text-[#C5A059] text-xs uppercase tracking-[0.25em] border border-[#C5A059] px-6 py-3 hover:bg-[#C5A059] hover:text-black transition-colors">{t("teampage.join")}</Link>
      </div>
    );
  }

  const bySlot = {};
  team.joueurs.forEach((j) => { (bySlot[j.slot] = bySlot[j.slot] || []).push(j); });
  const counts = {};

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white grain">
      <header className="border-b border-[#27272A] bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="team-logo-home"><img src="/assets/logo-dark-sm.png" alt="D2R" className="h-9 w-auto" /></Link>
          <Link to="/" data-testid="team-back-link" className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-[#C5A059] transition-colors uppercase tracking-[0.2em]">
            <ArrowLeft size={14} /> {t("teampage.back")}
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("teampage.eyebrow")}</p>
          <div className="flex flex-wrap items-end gap-6">
            <h1 className="font-display text-5xl md:text-6xl uppercase font-bold tracking-tight">{team.nom}</h1>
            <span className={`mb-2 text-[10px] uppercase tracking-[0.2em] px-4 py-2 border ${team.statut === "complete" ? "border-[#C5A059] text-[#C5A059]" : "border-[#27272A] text-[#71717A]"}`}>
              {team.statut === "complete" ? t("teampage.complete") : `${t("teampage.forming")} — ${team.joueurs.length}/11`}
            </span>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 mt-14">
            {[
              { icon: User, label: t("teampage.coach"), value: team.coach || t("teampage.tbd") },
              { icon: Clock, label: t("teampage.slot"), value: team.creneau || t("teampage.tbc") },
              { icon: CalendarDays, label: t("teampage.match"), value: team.match_prevu || t("teampage.whenfull") },
            ].map((c) => (
              <div key={c.label} data-testid={`team-info-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`} className="border border-[#27272A] bg-[#121214] p-8">
                <c.icon size={22} className="text-[#C5A059]" strokeWidth={1.5} />
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mt-5">{c.label}</p>
                <p className="font-display text-xl uppercase font-medium mt-2">{c.value}</p>
              </div>
            ))}
          </div>

          <h2 className="font-display text-3xl uppercase font-bold mt-20 mb-8">{t("teampage.roster.a")} <span className="text-[#C5A059]">{t("teampage.roster.b")}</span></h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-roster">
            {SLOTS.map((slot, i) => {
              counts[slot] = counts[slot] || 0;
              const j = (bySlot[slot] || [])[counts[slot]];
              counts[slot]++;
              const a = j ? age(j.date_naissance) : null;
              return (
                <div key={i} className={`border p-5 ${j ? "border-[#27272A] bg-[#121214]" : "border-dashed border-[#27272A]"}`}>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{SLOT_LABELS[slot]}</p>
                  {j ? (
                    <>
                      <p className="font-display text-lg uppercase font-medium mt-2">{j.prenom} {j.nom}</p>
                      <p className="text-xs text-[#71717A] mt-1">
                        {[a ? `${a} ${t("teampage.years")}` : null, j.club].filter(Boolean).join(" · ") || "Académie D2R"}
                      </p>
                      {j.stats && (j.stats.matchs > 0 || j.stats.buts > 0 || j.stats.passes > 0) && (
                        <div className="flex gap-4 mt-3 pt-3 border-t border-[#27272A]">
                          {[[t("lb.matches"), j.stats.matchs], [t("lb.goals"), j.stats.buts], [t("lb.assists.short"), j.stats.passes]].map(([label, val]) => (
                            <div key={label}>
                              <p className="font-display text-lg text-[#C5A059] font-medium">{val}</p>
                              <p className="text-[9px] uppercase tracking-widest text-[#71717A]">{label}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {j.note_coach && (
                        <p className="font-editorial italic text-xs text-[#A1A1AA] mt-3 leading-relaxed">"{j.note_coach}"</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-[#3f3f46] mt-3">{t("teampage.free")}</p>
                  )}
                </div>
              );
            })}
          </div>

          {team.notification && (
            <p className="mt-12 font-editorial italic text-[#C5A059] text-lg">"{team.notification}"</p>
          )}

          {team.statut !== "complete" && (
            <div className="mt-14 border border-[#27272A] bg-[#121214] p-10 flex flex-wrap items-center justify-between gap-6">
              <p className="text-[#A1A1AA] text-sm max-w-md">{t("teampage.open")}</p>
              <Link to="/detection" data-testid="team-cta-join" className="bg-[#C5A059] text-black px-8 py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors">
                {t("teampage.join")}
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
